import React from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import type { Currency } from 'dinero.js'
import { PayrollConceptFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import ShowStatus, {
  PaymentStatus,
} from 'modules/payroll_bundle/components/common/components/ShowStatus'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { DEFAULT_RESULT_CONCEPT, resultConceptsLabels } from 'modules/payroll_bundle/lib/constants'
import { ResultRowData } from 'modules/payroll_bundle/lib/resultsTableData'

import ResultConceptListItem from './ResultConceptListItem'

interface Props {
  resultRowData: ResultRowData
  policyPeriodIds: { current: number; previous: number }
  paymentStatus: PaymentStatus
  currency: Currency
}

const VISIBLE_CONCEPTS: string[] = [
  DEFAULT_RESULT_CONCEPT.net_pay,
  DEFAULT_RESULT_CONCEPT.company_cost,
  DEFAULT_RESULT_CONCEPT.gross_pay,
]

const ResultsTabPaymentTypeBlock = ({
  resultRowData,
  policyPeriodIds,
  paymentStatus,
  currency,
}: Props) => {
  const paymentTypeLabels = resultConceptsLabels()
  const { loading: loadingConcepts, concepts: mappedConcepts } = useConcepts(
    policyPeriodIds.current
  )

  const [showFullList, toggleShowFullList] = React.useState<boolean>(false)
  const { visibleConcepts, hiddenConcepts } = mappedConcepts.reduce(
    (acc, concept) => {
      if (VISIBLE_CONCEPTS.includes(concept.name)) {
        acc.visibleConcepts.push(concept)
      } else {
        acc.hiddenConcepts.push(concept)
      }

      return acc
    },
    { visibleConcepts: [], hiddenConcepts: [] } as {
      visibleConcepts: PayrollConceptFragment[]
      hiddenConcepts: PayrollConceptFragment[]
    }
  )

  function handleListToggle() {
    toggleShowFullList((prevState) => !prevState)
  }

  return (
    <Box key={resultRowData.id} gap="s16" paddingX="s24" paddingY="s16">
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Text.Normal weight="semibold" color="grey1000">
          {paymentTypeLabels[resultRowData.paymentType]}
        </Text.Normal>
        <ShowStatus status={paymentStatus} />
      </Box>

      <Box
        border={{
          all: {
            width: 's1',
            color: 'grey400',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs006',
        }}
      >
        <Box overflowY="hidden">
          {visibleConcepts.map((concept) => (
            <ResultConceptListItem
              key={`${resultRowData.id}:${concept.id}`}
              concept={concept}
              resultRowData={resultRowData}
              currency={currency}
              policyPeriodIds={policyPeriodIds}
            />
          ))}

          <Box height={showFullList ? 'fit-content' : 's0'}>
            {hiddenConcepts.map((concept) => (
              <ResultConceptListItem
                key={`${resultRowData.id}:${concept.id}`}
                concept={concept}
                resultRowData={resultRowData}
                currency={currency}
                policyPeriodIds={policyPeriodIds}
              />
            ))}
          </Box>
        </Box>
        {hiddenConcepts.length > 0 && (
          <Box onClick={handleListToggle} background="grey200">
            <Box paddingX="s16" paddingY="s8" gap="s4" flexDirection="row" alignItems="center">
              <Text size="200" color="grey800" weight="medium">
                {showFullList
                  ? i18n.t('payroll_bundle.results_payments.results.list.hide_details')
                  : i18n.t('payroll_bundle.results_payments.results.list.show_details')}
              </Text>
              <Icon.Small icon={showFullList ? 'chevron-up' : 'chevron-down'} color="grey800" />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ResultsTabPaymentTypeBlock
